import React, { useState } from 'react'
import { BrandPlanIntegrations } from 'types'
import { AlertTriangle, Check2 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { TacticIntegrationDropdown } from '../TacticIntegrationDropdown'
import { TacticIntegrationListVariant } from '../TacticIntegrationList'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Avatar from 'common/Avatar/Avatar'
import { getInitials, pluralize } from 'utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { integrationIsInBrandPlan, tacticHasGenericIntegration } from '../utils'
import { NoContentInBrandPlan } from './NoContentInBrandPlan'
import { SwitchPlatformMenu } from '../SwitchPlatformMenu'
import { useAddAdditionalAccount } from 'hooks/useAddAdditionalAccount'
import Styles from './TacticIntegration.module.scss'
import cx from 'classnames'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'

interface MultiAccountIntegrationProps {
  IconComponent: React.ComponentType<any> | null
  integrations: ExternalIntegration[]
  index: number
  variant?: TacticIntegrationListVariant
  tactic?: BrandPlanIntegrations
}

export const MultiAccountIntegration: React.FC<MultiAccountIntegrationProps> = ({
  IconComponent,
  integrations,
  index,
  variant,
  tactic,
}) => {
  const { isIntegrationDisconnected } = useIntegrationHeartbeatStatus()
  const { data: brandPlan } = useCurrentBrandPlan()
  const provider = integrations[0]?.provider
  const providerName = provider ? ProviderToName[provider] : ''
  const numAccounts = integrations.length

  const handleAddAccount = useAddAdditionalAccount({ provider })

  // Track hover state and menu open state for each card
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null)
  const [openMenuCardId, setOpenMenuCardId] = useState<number | null>(null)

  const firstIntegration = integrations[0]

  return (
    <div className={cx(Styles.multiAccountIntegration, Styles.isConnected)}>
      <div className={Styles.multiAccountHeader}>
        <Text variant="smallList">
          <div className={Styles.indexIndicator}>{index} </div>
        </Text>
        <div className={Styles.multiAccountHeaderContent}>
          {IconComponent && (
            <div className={Styles.tacticIcon}>
              <IconComponent size={35} />
            </div>
          )}
          <Text variant="largeList" color="var(--dark-80)">
            {numAccounts} {providerName} {pluralize('account', numAccounts)} connected
          </Text>
        </div>
        <div className={Styles.multiAccountHeaderActions}>
          {variant === 'integrations_page' &&
            !integrationIsInBrandPlan(firstIntegration, brandPlan) &&
            firstIntegration && <NoContentInBrandPlan integration={firstIntegration} />}
          <InitiateOauthTrigger provider={provider as any} popUp onInitiatedOauth={handleAddAccount}>
            <Button variant="tertiary" size="sm" frontIcon={UserProfileAdd2}>
              Add Extra Account
            </Button>
          </InitiateOauthTrigger>
          {tacticHasGenericIntegration(tactic) && integrations.length <= 1 && firstIntegration && (
            <SwitchPlatformMenu integration={firstIntegration} tactic={tactic} />
          )}
        </div>
      </div>

      <div
        className={Styles.multiAccountGrid}
        style={{
          gridTemplateColumns: integrations.length <= 2 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
        }}
      >
        {integrations.map((integration) => {
          const { name, avatar, email, username } = extractProfileInfo(integration)
          const subName = email || username
          const isDisconnected = isIntegrationDisconnected(integration)
          const isHovered = hoveredCardId === integration.id
          const isMenuOpen = openMenuCardId === integration.id
          const showSettingsButton = isHovered || isMenuOpen

          return (
            <div
              key={integration.id}
              className={cx(Styles.multiAccountCard, {
                [Styles.multiAccountCardDisconnected]: isDisconnected,
              })}
              onMouseEnter={() => setHoveredCardId(integration.id)}
              onMouseLeave={() => setHoveredCardId(null)}
            >
              <div className={Styles.multiAccountCardAvatar}>
                <Avatar size={40} label={getInitials(name)} url={avatar || Silhouette} />
                {isDisconnected ? (
                  <div className={Styles.multiAccountCardError}>
                    <AlertTriangle size={12} color="var(--light-100)" />
                  </div>
                ) : (
                  <div className={Styles.multiAccountCardCheckmark}>
                    <Check2 size={12} color="var(--light-100)" />
                  </div>
                )}
              </div>
              <div className={Styles.multiAccountCardInfo}>
                <Text variant="smallList" color="var(--dark-80)" lineClamp={1}>
                  {name}
                </Text>
                {subName && (
                  <Text variant="metadata" color="var(--dark-40)" lineClamp={1}>
                    {subName}
                  </Text>
                )}
              </div>
              <div className={cx(Styles.settingsButton, { [Styles.settingsButtonVisible]: showSettingsButton })}>
                <TacticIntegrationDropdown
                  integration={integration}
                  variant={variant}
                  refreshColor={isDisconnected ? 'var(--red-50)' : undefined}
                  onMenuOpenChange={(isOpen) => setOpenMenuCardId(isOpen ? integration.id : null)}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
